/**
 * A balance that changes counts to its new value instead of jumping
 * (2026-10-09, "make all possible elements animated").
 *
 * The final text is written up front and the tween only ever overwrites it
 * with intermediate numbers, so anything that reads the element mid-tween —
 * a test, a screen reader, a re-render that wins the race — sees a real
 * value. Reduced motion skips straight to the end.
 */

const DURATION_MS = 650;

/** Whole integers only — `8/25` and `4.5k` are left to snap. */
export function parseCount(text: string | null | undefined): number | null {
  if (!text || !/^-?\d+$/.test(text.trim())) return null;
  return Number(text.trim());
}

/** easeOutCubic: fast away from the old number, settling onto the new one. */
export function easeOut(t: number): number {
  return 1 - (1 - t) ** 3;
}

function reducedMotion(): boolean {
  return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export function countUp(el: HTMLElement, from: number, to: number): void {
  el.textContent = String(to);
  if (from === to || reducedMotion()) return;

  // Restart the glow even if the last one has not finished.
  el.classList.remove('animate-tick-glow');
  void el.offsetWidth;
  el.classList.add('animate-tick-glow');

  const start = performance.now();
  const step = (now: number): void => {
    if (!el.isConnected) return;
    const t = Math.min(1, (now - start) / DURATION_MS);
    el.textContent = String(Math.round(from + (to - from) * easeOut(t)));
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/**
 * For a container that is rebuilt with `innerHTML`: read the numbers it shows
 * now, let `render` replace it, then tween each `[data-count]` that changed.
 */
export function renderWithCountUp(container: HTMLElement, render: () => void): void {
  const before = new Map<string, number>();
  container.querySelectorAll<HTMLElement>('[data-count]').forEach((el) => {
    const n = parseCount(el.textContent);
    if (n !== null) before.set(el.dataset.count!, n);
  });
  render();
  container.querySelectorAll<HTMLElement>('[data-count]').forEach((el) => {
    const from = before.get(el.dataset.count!);
    const to = parseCount(el.textContent);
    if (from !== undefined && to !== null) countUp(el, from, to);
  });
}
